// Story 10.2 — Badge de estado del bot. Color codificado.

import type { BotStatus } from '@/hooks/realtime-reducer';
import { cn } from '@/lib/utils';

interface Props {
  status: BotStatus;
  pulse?: boolean;
  className?: string;
}

const STYLES: Record<BotStatus, string> = {
  active: 'bg-success/15 text-success border-success/30',
  inactive: 'bg-muted text-muted-foreground border-border',
  error: 'bg-destructive/15 text-destructive border-destructive/30',
  deploying: 'bg-warning/15 text-warning border-warning/30',
};

const LABELS: Record<BotStatus, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  error: 'Error',
  deploying: 'Desplegando',
};

export function BotStatusBadge({ status, pulse = false, className }: Props): React.ReactElement {
  return (
    <span
      // P-242: badge estática NO debe ser `role="status"` — `aria-live="polite"`
      // hace que cada card anuncie en mount, generando spam con N cards. El
      // contenido textual ("Activo"/"Error") es ya legible por SR; el `title`
      // basta como tooltip visual.
      title={`Estado del bot: ${LABELS[status]}`}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium',
        STYLES[status],
        pulse && 'animate-pulse',
        className,
      )}
    >
      <span
        aria-hidden
        className={cn(
          'h-1.5 w-1.5 rounded-full',
          status === 'active' && 'bg-success',
          status === 'inactive' && 'bg-muted-foreground',
          status === 'error' && 'bg-destructive',
          status === 'deploying' && 'bg-warning',
        )}
      />
      {LABELS[status]}
    </span>
  );
}
